import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as procService from "../../services/procService";
import * as hrmsService from "../../services/hrmsService";
import { ProcBadge, fileSize, openSigned, today } from "./procUi";
import { AppDropdown, EmptyState, Field, LoadState, PageHeader, PrimaryButton, SecondaryButton, cardStyle, inputStyle, pageStyle, useLoad } from "../../components/ui";

// Create or change an RFQ: what you need (lines), when, whom to ask, which documents to ask for.
// A draft can be changed freely. Once it is live, lines and the award type lock when a supplier has quoted;
// the deadline can only move later and suppliers can only be added.

const TYPES = [
  { label: "Lumpsum: the whole RFQ goes to one supplier", value: "LUMPSUM" },
  { label: "Item based: each item can go to a different supplier", value: "ITEM" },
  { label: "Split: an item's quantity can be shared between suppliers", value: "SPLIT" },
];
const OTHER = "__OTHER__";
let keySeq = 0;
const nextKey = () => `k${++keySeq}`;
const blankLine = () => ({ key: nextKey(), id: null, line_no: null, category_id: null, item_id: null, item: "", brand: "", uom: "", quantity: "", price: "", description: "", stock: null, stockPick: null });
const blankNeed = () => ({ key: nextKey(), id: null, type: OTHER, document: "", rfq_param: "", is_mandatory: true });
const supplierKey = (s) => `${s.supplier_name}|${s.company_name}`;
const addDays = (ymd, n) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// Stock item search (server-side as you type). Picking one fills the line from Inventory.
function StockItemPicker({ id, value, onPick }) {
  const [rows, setRows] = useState([]);
  const timer = useRef(null);
  const load = (q) =>
    procService
      .searchStockItems(q)
      .then((res) => setRows(res?.data || []))
      .catch(() => setRows([]));
  useEffect(() => {
    load("");
  }, []);
  const options = rows.map((r) => ({ label: `${r.sku} – ${r.name}`, value: r.id, description: r.unit_of_measure }));
  return (
    <AppDropdown
      id={id}
      value={value}
      filter
      showClear
      options={options}
      placeholder="From stock (optional)"
      onFilter={(e) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => load(e.filter), 300);
      }}
      onChange={(e) => onPick(rows.find((r) => r.id === e.value) || null)}
    />
  );
}

function Files({ rfqId, lineNo = null, lineId = null, files, editable, canRemove, onChanged }) {
  const mine = files.filter((f) => (lineNo ? f.owner_type === "RFQ_LINE_ATTACHMENT" && f.line_no === lineNo : f.owner_type === "RFQ_ATTACHMENT"));
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const res = await procService.uploadRfqAttachment(rfqId, file, lineId);
      if (res?.success) {
        toast.success("File attached");
        onChanged();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (f) => {
    try {
      const res = await procService.removeRfqAttachment(rfqId, f.id);
      if (res?.success === false) toast.error(hrmsService.responseMessage(res));
      else onChanged();
    } catch (err) {
      toast.error(err.message || "The file could not be removed");
    }
  };
  return (
    <div style={{ fontSize: 13 }}>
      {mine.map((f) => (
        <div key={f.id} className="d-flex align-items-center gap-2 mb-1">
          <button type="button" className="btn btn-link p-0" style={{ fontSize: 13 }} onClick={() => openSigned(() => procService.getRfqAttachmentUrl(rfqId, f.id), toast)}>
            {f.file_name}
          </button>
          <span style={{ color: "#6B7280" }}>{fileSize(f.size)}</span>
          {canRemove ? (
            <button type="button" className="btn btn-link p-0 text-danger" style={{ fontSize: 13 }} onClick={() => remove(f)}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      {editable ? (
        <label className="btn btn-outline-secondary btn-sm mb-0" style={{ opacity: busy ? 0.6 : 1 }}>
          {busy ? "Uploading…" : lineNo ? "Attach a file to this line" : "Attach a file"}
          <input type="file" hidden disabled={busy} accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={pick} aria-label={lineNo ? `Attach a file to line ${lineNo}` : "Attach a file"} />
        </label>
      ) : null}
    </div>
  );
}

const section = (title, children, hint) => (
  <div style={{ ...cardStyle, padding: 16, marginBottom: 16 }}>
    <h6 className="mb-1">{title}</h6>
    {hint ? <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>{hint}</div> : <div className="mb-3" />}
    {children}
  </div>
);

// What the form starts from: the saved RFQ, or the buyer's defaults for a new one. Ids are encrypted afresh in each answer,
// so the saved RFQ's category is matched to the category list by NAME.
function startingForm(d, settings, warehouses) {
  if (d) {
    return {
      title: d.title,
      rfq_type: d.rfq_type,
      min_split_pct: d.min_split_pct,
      responsible_date: d.responsible_date || "",
      delivery_date: d.delivery_date || "",
      location: d.location || "",
      instructions: d.instructions || "",
      warehouse_id: (warehouses.find((w) => w.id === d.warehouse_id) || {}).id || null,
    };
  }
  const s = settings || {};
  return {
    title: "",
    rfq_type: s.default_rfq_type || "LUMPSUM",
    min_split_pct: s.default_min_split_pct ?? 10,
    responsible_date: addDays(today(), Number(s.default_response_days) || 7),
    delivery_date: "",
    location: "",
    instructions: "",
    warehouse_id: s.default_warehouse_id ?? null,
  };
}

function startingLines(d, catOptions) {
  if (!d || !d.lines.length) return [blankLine()];
  return d.lines.map((l) => ({
    key: nextKey(),
    id: l.id,
    line_no: l.line_no,
    category_id: (catOptions.find((o) => o.label === l.category_name) || {}).value || null,
    item_id: l.item_id,
    item: l.item || "",
    brand: l.brand || "",
    uom: l.uom || "",
    quantity: l.quantity,
    price: l.price ?? "",
    description: l.description || "",
    stock: l.item_id ? { label: l.item_sku } : null,
    stockPick: null,
  }));
}

// The form itself. It is mounted only once everything it needs has loaded, so its starting state is plain props.
function RfqEditor({ id, d, reload, categories, docTypes, warehouses, settings }) {
  const navigate = useNavigate();
  const catOptions = useMemo(() => categories.map((c) => ({ label: c.name, value: c.id })), [categories]);
  const rfqTypes = docTypes.filter((t) => t.applies_to === "RFQ");
  const typeOptions = [...rfqTypes.map((t) => ({ label: t.name, value: t.code })), { label: "Other (type a name)", value: OTHER }];

  const [form, setForm] = useState(() => startingForm(d, settings, warehouses));
  const [lines, setLines] = useState(() => startingLines(d, catOptions));
  const [needs, setNeeds] = useState(() => (d ? d.required_documents.map((n) => ({ key: nextKey(), id: n.id, type: n.document_type_code || OTHER, document: n.document, rfq_param: n.rfq_param || "", is_mandatory: !!n.is_mandatory })) : []));
  const [picked, setPicked] = useState(() => (d ? d.suppliers.map(supplierKey) : [])); // supplier keys (name | company)
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const catKey = lines.map((l) => l.category_id).filter(Boolean).join(",");
  const eligible = useLoad(() => procService.getEligibleSuppliers(catKey ? catKey.split(",") : []), [catKey]);
  const live = !!d && d.status === 1;
  const locked = !!d && d.lines_locked;
  const invited = new Set(live ? d.suppliers.map(supplierKey) : []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const setNeed = (key, patch) => setNeeds((ns) => ns.map((n) => (n.key === key ? { ...n, ...patch } : n)));

  const pickStock = (line, item) => {
    if (!item) return setLine(line.key, { item_id: null, stock: null, stockPick: null });
    // the item's category arrives with a different encryption than the category list: matched by name
    setLine(line.key, { item_id: item.id, stock: { label: item.sku }, stockPick: item.id, item: item.name, uom: item.unit_of_measure || line.uom, category_id: (catOptions.find((o) => o.label === item.category_name) || {}).value || line.category_id });
  };

  const eligibleList = eligible.data || [];
  const shown = eligibleList.filter((s) => !filter.trim() || `${s.supplier_name} ${s.company_name}`.toLowerCase().includes(filter.trim().toLowerCase()));
  const togglePick = (s) => setPicked((p) => (p.includes(supplierKey(s)) ? p.filter((k) => k !== supplierKey(s)) : [...p, supplierKey(s)]));
  const pickMatching = () => setPicked((p) => [...new Set([...p, ...eligibleList.filter((s) => s.matches).map(supplierKey)])]);

  const payload = () => ({
    title: form.title,
    rfq_type: form.rfq_type,
    ...(form.rfq_type === "SPLIT" ? { min_split_pct: form.min_split_pct } : {}),
    responsible_date: form.responsible_date,
    delivery_date: form.delivery_date,
    location: form.location,
    instructions: form.instructions,
    warehouse_id: form.warehouse_id,
    ...(locked
      ? {}
      : {
          lines: lines.map((l) => ({ id: l.id || undefined, category_id: l.category_id, item_id: l.item_id, item: l.item, brand: l.brand, uom: l.uom, quantity: l.quantity, price: l.price, description: l.description })),
        }),
    required_documents: needs
      .filter((n) => n.document.trim() || n.type !== OTHER)
      .map((n) => ({ id: n.id || undefined, document_type_code: n.type === OTHER ? null : n.type, document: n.type === OTHER ? n.document : n.document || (rfqTypes.find((t) => t.code === n.type) || {}).name, rfq_param: n.rfq_param, is_mandatory: n.is_mandatory })),
    supplier_ids: eligibleList.filter((s) => picked.includes(supplierKey(s))).map((s) => s.supplier_id),
  });

  const persist = async () => {
    const res = await procService.saveRfq(id, payload());
    if (!res?.success) {
      toast.error(hrmsService.responseMessage(res));
      return null;
    }
    return res.data;
  };
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };
  const saveDraft = () =>
    run(async () => {
      const saved = await persist();
      if (!saved) return;
      toast.success("Draft saved");
      if (!id) navigate(`/proc/rfqs/${saved.id}/edit`, { replace: true });
      else {
        reload();
      }
    });
  const publish = () =>
    run(async () => {
      const saved = await persist();
      if (!saved) return;
      const res = await procService.publishRfq(saved.id);
      if (!res?.success) {
        toast.error(hrmsService.responseMessage(res));
        if (!id) navigate(`/proc/rfqs/${saved.id}/edit`, { replace: true });
        return;
      }
      toast.success(res.message || "RFQ sent to suppliers");
      navigate(`/proc/rfqs/${saved.id}`);
    });
  const saveLive = () =>
    run(async () => {
      const saved = await persist();
      if (!saved) return;
      toast.success("RFQ saved");
      navigate(`/proc/rfqs/${saved.id}`);
    });

  const closed = !!d && ![0, 1].includes(d.status);
  const files = d ? d.attachments : [];

  return (
    <>
      {closed ? (
        <EmptyState title="This RFQ can no longer be changed" text="It is closed or has been awarded." action={<SecondaryButton onClick={() => navigate(`/proc/rfqs/${id}`)}>Back to the RFQ</SecondaryButton>} />
      ) : (
        <>
          {section(
            "Details",
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0 16px" }}>
              <Field label="Title" required htmlFor="rfq-title">
                <input id="rfq-title" className="form-control" style={inputStyle} maxLength={200} value={form.title} onChange={(e) => set("title")(e.target.value)} />
              </Field>
              <Field label="How is it awarded?" required htmlFor="rfq-type" hint={locked ? "Fixed: a supplier has quoted" : undefined}>
                <AppDropdown id="rfq-type" value={form.rfq_type} options={TYPES} disabled={locked} onChange={(e) => set("rfq_type")(e.value)} />
              </Field>
              {form.rfq_type === "SPLIT" ? (
                <Field label="Smallest share of an item (%)" htmlFor="rfq-split" hint="A supplier cannot be given less than this share of an item">
                  <input id="rfq-split" type="number" min={0} max={100} className="form-control" style={inputStyle} value={form.min_split_pct ?? ""} onChange={(e) => set("min_split_pct")(e.target.value)} />
                </Field>
              ) : null}
              <Field label="Suppliers must quote by" required htmlFor="rfq-deadline" hint="The last day suppliers can send a quotation">
                <input id="rfq-deadline" type="date" className="form-control" style={inputStyle} value={form.responsible_date} onChange={(e) => set("responsible_date")(e.target.value)} />
              </Field>
              <Field label="Delivery date" htmlFor="rfq-delivery">
                <input id="rfq-delivery" type="date" className="form-control" style={inputStyle} value={form.delivery_date} onChange={(e) => set("delivery_date")(e.target.value)} />
              </Field>
              <Field label="Deliver to" htmlFor="rfq-warehouse" hint="The warehouse the goods are received into">
                <AppDropdown id="rfq-warehouse" value={form.warehouse_id} showClear options={(warehouses.data || []).map((w) => ({ label: w.name, value: w.id }))} onChange={(e) => set("warehouse_id")(e.value ?? null)} placeholder="Default warehouse" />
              </Field>
              <Field label="Delivery location" htmlFor="rfq-location">
                <input id="rfq-location" className="form-control" style={inputStyle} maxLength={300} value={form.location} onChange={(e) => set("location")(e.target.value)} />
              </Field>
            </div>
          )}
          {section(
            "Instructions for suppliers",
            <textarea id="rfq-instructions" aria-label="Instructions for suppliers" className="form-control" rows={3} maxLength={2000} value={form.instructions} onChange={(e) => set("instructions")(e.target.value)} />
          )}

          {section(
            "What you need",
            <>
              {locked ? <div style={{ fontSize: 13, color: "#92400E", marginBottom: 12 }}>A supplier has already quoted, so the lines can no longer be changed.</div> : null}
              {lines.map((l, i) => (
                <div key={l.key} style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div className="d-flex align-items-center mb-2">
                    <strong style={{ fontSize: 13 }}>Line {i + 1}</strong>
                    {!locked && lines.length > 1 ? (
                      <button type="button" className="btn btn-link text-danger ms-auto p-0" style={{ fontSize: 13 }} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                        Remove line {i + 1}
                      </button>
                    ) : null}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 16px" }}>
                    <Field label="Category" htmlFor={`rfq-cat-${i}`}>
                      <AppDropdown id={`rfq-cat-${i}`} value={l.category_id} showClear filter={catOptions.length > 8} disabled={locked} options={catOptions} onChange={(e) => setLine(l.key, { category_id: e.value ?? null })} placeholder="Choose" />
                    </Field>
                    <Field label="Stock item" htmlFor={`rfq-stock-${i}`} hint="Leave empty for something you do not keep in stock">
                      {locked ? (
                        <div style={{ fontSize: 14 }}>{l.stock ? l.stock.label : "—"}</div>
                      ) : (
                        <>
                          <StockItemPicker id={`rfq-stock-${i}`} value={l.stockPick} onPick={(item) => pickStock(l, item)} />
                          {l.stock && !l.stockPick ? <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>Linked to stock item {l.stock.label}</div> : null}
                        </>
                      )}
                    </Field>
                    <Field label="Item" required htmlFor={`rfq-item-${i}`}>
                      <input id={`rfq-item-${i}`} className="form-control" style={inputStyle} maxLength={200} disabled={locked} value={l.item} onChange={(e) => setLine(l.key, { item: e.target.value })} />
                    </Field>
                    <Field label="Brand" htmlFor={`rfq-brand-${i}`}>
                      <input id={`rfq-brand-${i}`} className="form-control" style={inputStyle} maxLength={100} disabled={locked} value={l.brand} onChange={(e) => setLine(l.key, { brand: e.target.value })} />
                    </Field>
                    <Field label="Quantity" required htmlFor={`rfq-qty-${i}`}>
                      <input id={`rfq-qty-${i}`} type="number" min={0} className="form-control" style={inputStyle} disabled={locked} value={l.quantity} onChange={(e) => setLine(l.key, { quantity: e.target.value })} />
                    </Field>
                    <Field label="Unit" htmlFor={`rfq-uom-${i}`}>
                      <input id={`rfq-uom-${i}`} className="form-control" style={inputStyle} maxLength={30} disabled={locked} value={l.uom} onChange={(e) => setLine(l.key, { uom: e.target.value })} />
                    </Field>
                    <Field label="Your estimate per unit" htmlFor={`rfq-price-${i}`} hint="Only you see this">
                      <input id={`rfq-price-${i}`} type="number" min={0} className="form-control" style={inputStyle} disabled={locked} value={l.price} onChange={(e) => setLine(l.key, { price: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Specification" htmlFor={`rfq-desc-${i}`}>
                    <textarea id={`rfq-desc-${i}`} className="form-control" rows={2} maxLength={1000} disabled={locked} value={l.description} onChange={(e) => setLine(l.key, { description: e.target.value })} />
                  </Field>
                  {l.id && d ? <Files rfqId={id} lineNo={l.line_no} lineId={l.id} files={files} editable={!closed} canRemove={d.status === 0} onChanged={reload} /> : null}
                </div>
              ))}
              {!locked ? (
                <SecondaryButton onClick={() => setLines((ls) => [...ls, blankLine()])}>Add a line</SecondaryButton>
              ) : null}
              {!id ? <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>Save the draft first to attach files to a line.</div> : null}
            </>
          )}

          {section(
            "Suppliers",
            <>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <input className="form-control" style={{ ...inputStyle, maxWidth: 280 }} placeholder="Search suppliers" aria-label="Search suppliers" value={filter} onChange={(e) => setFilter(e.target.value)} />
                <SecondaryButton onClick={pickMatching} disabled={!eligibleList.some((s) => s.matches)}>
                  Select suppliers of these categories
                </SecondaryButton>
              </div>
              <LoadState loading={eligible.loading && !eligible.data} error={eligible.error} onRetry={eligible.reload}>
                {eligibleList.length === 0 ? (
                  <EmptyState title="No active suppliers yet" text="Invite a supplier under Suppliers. Once they accept, you can ask them to quote." />
                ) : (
                  shown.map((s) => {
                    const inviteLocked = invited.has(supplierKey(s));
                    const checked = picked.includes(supplierKey(s));
                    return (
                      <label key={supplierKey(s)} className="d-flex align-items-start gap-2 mb-2" style={{ fontSize: 14, cursor: inviteLocked ? "default" : "pointer" }}>
                        <input type="checkbox" className="form-check-input mt-1" checked={checked} disabled={inviteLocked} onChange={() => togglePick(s)} aria-label={s.supplier_name} />
                        <span>
                          <strong>{s.supplier_name}</strong>
                          {s.company_name !== s.supplier_name ? <span style={{ color: "#6B7280" }}> · {s.company_name}</span> : null}
                          <span className="ms-2">{s.matches ? <ProcBadge status="ACTIVE" label="Supplies these items" /> : null}</span>
                          {s.compliance !== "COMPLETE" ? <span className="ms-2"><ProcBadge status={s.compliance} label={s.compliance === "EXPIRED" ? "A document expired" : "Documents pending"} /></span> : null}
                          <div style={{ fontSize: 12, color: "#6B7280" }}>{s.categories.map((c) => c.name).join(", ") || "No categories set"}</div>
                          {inviteLocked ? <div style={{ fontSize: 12, color: "#6B7280" }}>Already invited</div> : null}
                        </span>
                      </label>
                    );
                  })
                )}
              </LoadState>
            </>,
            live ? "Suppliers can be added to a live RFQ; they are invited at once." : "Choose who receives the RFQ when you publish it."
          )}

          {section(
            "Documents to send with the quotation",
            <>
              {needs.length === 0 ? <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>None requested.</div> : null}
              {needs.map((n, i) => (
                <div key={n.key} className="d-flex flex-wrap gap-2 align-items-end mb-2">
                  <div style={{ minWidth: 220 }}>
                    <AppDropdown id={`rfq-need-${i}`} value={n.type} options={typeOptions} onChange={(e) => setNeed(n.key, { type: e.value, document: e.value === OTHER ? n.document : "" })} aria-label={`Document ${i + 1}`} />
                  </div>
                  {n.type === OTHER ? <input className="form-control" style={{ ...inputStyle, maxWidth: 220 }} placeholder="Document name" aria-label={`Document ${i + 1} name`} maxLength={150} value={n.document} onChange={(e) => setNeed(n.key, { document: e.target.value })} /> : null}
                  <input className="form-control" style={{ ...inputStyle, flex: 1, minWidth: 200 }} placeholder="What exactly is wanted (optional)" aria-label={`Document ${i + 1} details`} maxLength={500} value={n.rfq_param} onChange={(e) => setNeed(n.key, { rfq_param: e.target.value })} />
                  <label className="form-check d-flex align-items-center gap-1 mb-2" style={{ fontSize: 13 }}>
                    <input type="checkbox" className="form-check-input" checked={n.is_mandatory} onChange={(e) => setNeed(n.key, { is_mandatory: e.target.checked })} /> Required
                  </label>
                  <button type="button" className="btn btn-link text-danger mb-1" onClick={() => setNeeds((ns) => ns.filter((x) => x.key !== n.key))}>
                    Remove
                  </button>
                </div>
              ))}
              <SecondaryButton onClick={() => setNeeds((ns) => [...ns, blankNeed()])}>Add a document</SecondaryButton>
            </>
          )}

          {section(
            "Files for suppliers",
            id && d ? <Files rfqId={id} files={files} editable canRemove={d.status === 0} onChanged={reload} /> : <div style={{ fontSize: 13, color: "#6B7280" }}>Save the draft first, then attach drawings or specifications here.</div>,
            "PDF, JPG, PNG or DOCX, up to 10 MB each."
          )}

          <div className="d-flex flex-wrap gap-2 justify-content-end mb-4">
            <SecondaryButton onClick={() => navigate(id ? `/proc/rfqs/${id}` : "/proc/rfqs")} disabled={busy}>
              Cancel
            </SecondaryButton>
            {live ? (
              <PrimaryButton onClick={saveLive} disabled={busy}>
                Save changes
              </PrimaryButton>
            ) : (
              <>
                <SecondaryButton onClick={saveDraft} disabled={busy}>
                  Save draft
                </SecondaryButton>
                <PrimaryButton onClick={publish} disabled={busy}>
                  Publish to suppliers
                </PrimaryButton>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default function RfqForm() {
  const { id } = useParams();
  const rfq = useLoad(() => (id ? procService.getRfq(id) : Promise.resolve({ success: true, data: null })), [id]);
  const categories = useLoad(() => procService.listCategories(), []);
  const docTypes = useLoad(() => procService.listMaster("document_types", { status: "ACTIVE" }), []);
  const warehouses = useLoad(() => procService.getWarehouses(), []);
  const settings = useLoad(() => procService.getSettings(), []);

  // The editor appears once, when the first load is complete, and is never unmounted by a later reload (it keeps what was typed).
  const loaded = !rfq.loading && !categories.loading && !docTypes.loading && !warehouses.loading && !settings.loading;
  const [shown, setShown] = useState(false);
  if (loaded && !shown) setShown(true);
  const d = rfq.data;
  const live = !!d && d.status === 1;

  return (
    <div style={pageStyle}>
      <PageHeader
        title={id ? (d && d.rfq_number ? `Change ${d.rfq_number}` : "Change RFQ") : "New RFQ"}
        subtitle={live ? "This RFQ is live: suppliers see your changes" : "Draft: nothing is sent to suppliers until you publish"}
        actions={d ? <ProcBadge status={d.status === 0 ? "DRAFT" : "LIVE"} label={d.status_label} /> : null}
      />
      <LoadState
        loading={!shown}
        error={rfq.error || categories.error || settings.error}
        onRetry={() => {
          rfq.reload();
          categories.reload();
          settings.reload();
        }}
      >
        {shown ? <RfqEditor key={id || "new"} id={id} d={d} reload={rfq.reload} categories={categories.data || []} docTypes={docTypes.data || []} warehouses={warehouses.data || []} settings={settings.data} /> : null}
      </LoadState>
    </div>
  );
}
